import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu in card footer
 *
 * @prop cardId - The id of a card (Generate automatically from parent card)
 * @prop isGroupPost - Is the owner of the card a group? (default false - a user is the owner)
 * @prop contactUrl - Url for contact form
 * @prop pinValue - Is the card pinned?
 * @prop subscribeValue - Is user subscribed to card?
 * @prop bookmarkValue - Has user bookmarked the card?
 * @prop contactOn - Show contact action
 * @prop pinOn - Show pin action
 * @prop editOn - Show edit/settings action
 * @prop deleteOn - Show delete action
 * @prop blockOn - Show block action
 * @prop unblockOn - Show unblock action
 * @prop duplicateOn - Show duplicate action
 * @prop adminLinkOn - Show admin action
 * @prop reportOn - Show report action
 * @prop pinUrl - Url for pin/unpin action
 * @prop duplicateUrl - Url for duplicate action
 * @prop blockUrl - Url for block action
 * @prop unblockUrl - Url for unblock action
 * @prop editUrl - Url for edit/settings action
 * @prop noCurrentUserUrl - Url for users that are not logged in. All actions will visit this url.
 * @prop isInitSubscriber - Was current user ever subscribed to the card?
 */

export default class CardMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static styles = [BaseMenu.styles, style];

  static properties = {
    cardId: { type: Number },
    pinValue: { type: Boolean },
    subscribeValue: { type: Boolean },
    bookmarkValue: { type: Boolean },
    isGroupPost: { type: Boolean },
    isInitSubscriber: { type: Boolean },

    // Permissions
    contactOn: { type: Boolean },
    pinOn: { type: Boolean },
    editOn: { type: Boolean },
    deleteOn: { type: Boolean },
    duplicateOn: { type: Boolean },
    adminLinkOn: { type: Boolean },
    blockOn: { type: Boolean },
    unblockOn: { type: Boolean },
    reportOn: { type: Boolean },

    // URLs
    contactUrl: { type: String },
    pinUrl: { type: String },
    duplicateUrl: { type: String },
    editUrl: { type: String },
    blockUrl: { type: String },
    unblockUrl: { type: String },
    noCurrentUserUrl: { type: String },

    // States
    card: { state: true },
    cardTitle: { state: true },
    type: { state: true },
  };

  constructor() {
    super();

    /** @internal */
    this.currentBrowserHref = window.location.href;
  }

  connectedCallback() {
    super.connectedCallback();
    this.card = document.querySelector(`xs-card[uid="${this.cardId}"]`);

    if (this.card) {
      this.type = this.card.cardType;
      this.cardTitle = this.card.querySelector('[slot="title"]')?.innerText;
      this.card.addEventListener('xs-card-attend', this);
      this.card.addEventListener('cardOption-change', this);
    }

    this.addEventListener('xs-card-menu-bookmark', this);
    window.addEventListener('xs-comment-form-submit', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('xs-comment-form-submit', this);

    if (this.card) {
      this.card.removeEventListener('xs-card-attend', this);
      this.card.removeEventListener('cardOption-change', this);
    }
  }

  willUpdate(changedProps) {
    super.willUpdate(changedProps);

    if (changedProps.has('subscribeValue') && this.subscribeValue)
      this.isInitSubscriber = true;
  }

  /** @internal */
  get instanceId() {
    return this.cardId;
  }

  /** @internal */
  get urlType() {
    return `${this.type}_cards`;
  }

  /** @internal */
  get baseUrl() {
    return `/${this.urlType}/${this.cardId}`;
  }

  /** @internal */
  get reportUrl() {
    return `/complaints/new?complaint[complainable_id]=${this.cardId}&complaint[complainable_type]=Card`;
  }

  /** @internal */
  get adminUrl() {
    return `/admin/${this.urlType}/${this.cardId}`;
  }

  /** @internal */
  get subscribeUrl() {
    return `/cards/${this.cardId}/toggle_subscribe`;
  }

  /** @internal */
  get bookmarkUrl() {
    return `/cards/${this.cardId}/toggle_bookmark`;
  }

  /** @internal */
  get deleteUrl() {
    return `${this.baseUrl}?trash=true`;
  }

  /** @internal */
  get isLoggedIn() {
    return Boolean(this.AppConfig.user?.id);
  }

  /** @internal */
  get subscribeItem() {
    return {
      title: this.textBaseOnActiveKey('actions.subscribe', 'subscribeValue'),
      action: this.actionFor('toggleSubscribe'),
      icon: this.subscribeValue ? 'bell:solid' : 'bell',
      partName: `subscribe ${this.subscribeValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get bookmarkItem() {
    return {
      title: this.textBaseOnActiveKey('actions.bookmark', 'bookmarkValue'),
      action: this.actionFor('toggleBookmark'),
      icon: this.bookmarkValue ? 'bookmark:solid' : 'bookmark',
      partName: `bookmark ${this.bookmarkValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get pinItem() {
    return {
      title: this.textBaseOnActiveKey('actions.pin', 'pinValue'),
      action: this.actionFor('togglePin'),
      icon: this.pinValue ? 'thumbtack:solid' : 'thumbtack',
      partName: `pin ${this.pinValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get contactItem() {
    return {
      title: this.t('actions.contact'),
      action: this.actionFor('cardContact', [this.contactUrl]),
      icon: 'comment-alt-lines',
      partName: 'contact',
    };
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.inappropriate_report'),
      action: this.actionFor('cardGet', [this.reportUrl]),
      icon: 'hexagon-exclamation',
      partName: 'report',
    };
  }

  /** @internal */
  get blockItem() {
    return {
      title: this.t(`actions.block.${this.isGroupPost ? 'group' : 'user'}`),
      action: this.actionFor('cardBlock'),
      icon: 'ban',
      partName: 'block',
    };
  }

  /** @internal */
  get unblockItem() {
    return {
      title: this.t(`actions.unblock.${this.isGroupPost ? 'group' : 'user'}`),
      action: this.actionFor('cardUnblock'),
      icon: 'ban:solid',
      partName: 'unblock',
    };
  }

  /** @internal */
  get settingsItem() {
    return {
      title: this.t('actions.settings'),
      action: this.actionFor('cardGet', [this.editUrl]),
      icon: 'cog',
      partName: 'edit',
    };
  }

  /** @internal */
  get duplicateItem() {
    return {
      title: this.t('actions.duplicate'),
      action: this.actionFor('cardGet', [this.duplicateUrl]),
      icon: 'copy',
      partName: 'duplicate',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('cardDelete'),
      role: 'destructive',
      icon: 'close',
      partName: 'delete destructive',
    };
  }

  /** @internal */
  get adminLinkItem() {
    return {
      title: this.t('actions.admin'),
      action: this.actionFor('cardVisit', [this.adminUrl]),
      icon: 'eye',
      partName: 'admin',
    };
  }

  /** @internal */
  get items() {
    return [
      this.subscribeItem,
      this.bookmarkItem,
      this.pinOn && this.pinItem,
      this.contactOn && this.contactItem,
      this.reportOn && this.reportItem,
      this.blockOn && this.blockItem,
      this.unblockOn && this.unblockItem,
      this.editOn && this.settingsItem,
      this.duplicateOn && this.duplicateItem,
      (this.deleteOn || this.adminLinkOn) && this.divider,
      this.deleteOn && this.deleteItem,
      this.adminLinkOn && this.adminLinkItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.cardTitle,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-card-menu',
      id: this.instanceId,
      method: this.isLoggedIn ? method : 'cardNoCurrentUser',
      args: args,
    };
  }

  activeKey(prop) {
    return this[prop] ? 'on' : 'off';
  }

  textBaseOnActiveKey(key, prop) {
    return this.t(`${key}.${this.activeKey(prop)}`);
  }

  toggleSubscribe() {
    this.remotePropToggle({
      url: this.subscribeUrl,
      prop: 'subscribeValue',
      key: 'snack.subscribe',
    });
  }

  async toggleBookmark() {
    await this.remotePropToggle({
      url: this.bookmarkUrl,
      prop: 'bookmarkValue',
      key: 'snack.bookmark',
    });

    this.dispatchEvent(
      new Event('xs-card-menu-bookmark', { bubbles: true, composed: true })
    );
  }

  async togglePin() {
    await this.remotePropToggle({
      url: this.pinUrl,
      prop: 'pinValue',
      key: 'snack.pin',
    });

    const cardHeader = this.card?.querySelector?.('xs-card-header');
    if (cardHeader) cardHeader.pinValue = this.pinValue;
  }

  async remotePropToggle({ url, prop, key }) {
    try {
      await this.commonActions.patch(url);
      this[prop] = !this[prop];
      this.commonActions.displaySnackBarSuccess(
        this.textBaseOnActiveKey(key, prop)
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  cardContact(url) {
    this.commonActions.execJSFromResponse(url);
  }

  cardNoCurrentUser() {
    this.cardVisit(this.noCurrentUserUrl);
  }

  cardVisit(url) {
    this.commonActions.visit(url);
  }

  async cardGet(url) {
    try {
      await this.commonActions.get(url);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async cardDelete() {
    if (!this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default')))
      return false;

    try {
      const res = await this.commonActions.delete(this.deleteUrl);
      const redirectUrl = res.headers.get('X-Xhr-Redirect');
      const isFullView = this.currentBrowserHref.includes(this.baseUrl);

      if (isFullView) {
        if (redirectUrl) this.commonActions.visit(redirectUrl);
        else document.querySelector('.js-back-or-default').click();

        return false;
      }

      this.dispatchEvent(
        new Event('xs-card-menu-before-remove', {
          bubbles: true,
          composed: true,
        })
      );

      this.card?.remove();

      this.commonActions.displaySnackBarSuccess(
        this.t('snack.card_delete_success')
      );
    } catch (e) {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async cardBlock() {
    if (
      !this.commonActions.confirmDialog(
        this.t(
          `confirm_dialog_msg.block.${this.isGroupPost ? 'group' : 'user'}`
        )
      )
    )
      return false;

    try {
      await this.commonActions.post(this.blockUrl);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async cardUnblock() {
    try {
      await this.commonActions.post(this.unblockUrl);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  handleEvent(e) {
    if (this.isInitSubscriber) return;

    if (
      e.type === 'xs-card-attend' ||
      e.type === 'xs-card-menu-bookmark' ||
      (e.type === 'cardOption-change' &&
        (this.card.cardType === 'organize' || this.card.cardType === 'date')) ||
      (e.type === 'xs-comment-form-submit' && +e.target.cardId === +this.cardId)
    )
      this.subscribeValue = true;
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <slot name="trigger" slot="trigger">
          <button class="xs-btn btn-naked-dark" aria-label="toggle button">
            <ef-icon name="ellipsis"></ef-icon>
          </button>
        </slot>
      </ef-dot-menu>
    `;
  }
}

CardMenu.registerTagName('xs-card-menu');
CardMenu.registerCommonActions('xs-card-menu');

customElements.get('xs-card-menu') ||
  customElements.define('xs-card-menu', CardMenu);
